import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CatalogueApiService } from '@angular-workspace/services';
import type { ModelMetadataInfo } from '../model.models';
import {
  AdditionalInfoComponent,
  type AdditionalInfoFeedbackItem,
} from '../../shared/additional-info/additional-info.component';
import type { TemplateAdditionalInfoState } from '../../shared/additional-info/additional-info.logic';

type DropdownKey = 'industry' | 'license' | 'modelType' | 'fileFormat';

interface SelectOption {
  value: string;
  label: string;
}

function tagsRequired(control: AbstractControl): ValidationErrors | null {
  const tags = control.value as string[] | null;
  if (!Array.isArray(tags) || tags.length === 0) {
    return { tagsRequired: true };
  }
  return null;
}

@Component({
  selector: 'lib-model-metadata-info',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AdditionalInfoComponent,
  ],
  templateUrl: './model-metadata-info.component.html',
  styleUrl: '../../dataset/ngsild/metadata-info/metadata-info.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'ngsild-widget-host' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelMetadataInfoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly validChange = output<boolean>();
  readonly valueChange = output<ModelMetadataInfo>();
  readonly additionalInfoRef = viewChild(AdditionalInfoComponent);

  readonly openDropdown = signal<DropdownKey | null>(null);
  tagInput = '';
  readonly tagMaxLength = 20;

  readonly industryOptions: SelectOption[] = [
    { value: 'Agriculture', label: 'Agriculture' },
    { value: 'forest', label: 'Forest' },
    { value: 'Water', label: 'Water' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Transport', label: 'Transport' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Other', label: 'Other' },
  ];

  readonly licenseOptions: SelectOption[] = [
    {
      value: 'Open Government License, India',
      label: 'Open Government License, India',
    },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'MIT', label: 'MIT' },
    { value: 'Other', label: 'Other' },
  ];

  modelTypeOptions: SelectOption[] = [];
  fileFormatOptions: SelectOption[] = [];

  readonly form = this.fb.nonNullable.group({
    industry: ['', Validators.required],
    license: ['', Validators.required],
    otherLicense: [''],
    modelType: ['', Validators.required],
    fileFormat: ['', Validators.required],
    tags: this.fb.nonNullable.control<string[]>([], [tagsRequired]),
  });

  ngOnInit(): void {
    void this.loadModelTypes();
    void this.loadFileFormats();

    this.form.controls.license.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((license) => {
        const otherLicenseControl = this.form.get('otherLicense');
        if (license === 'Other') {
          otherLicenseControl?.setValidators([Validators.required]);
        } else {
          otherLicenseControl?.clearValidators();
          otherLicenseControl?.setValue('');
        }
        otherLicenseControl?.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      });

    this.emitState();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitState());
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.validChange.emit(this.form.valid));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.openDropdown()) {
        this.openDropdown.set(null);
        this.cdr.markForCheck();
      }
    }
  }

  private async loadModelTypes(): Promise<void> {
    try {
      const searchCriteria = [
        { searchType: 'term', field: 'type', values: ['adex:AiModel'] },
      ];
      const response = await this.catalogueApi.getFilterList(
        searchCriteria as never,
        ['modelType'],
      );
      const values = this.extractFilterValues(response, 'modelType');
      if (values.length) {
        this.modelTypeOptions = values
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ value: v, label: v }));
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.warn('Failed to load model types:', error);
    }
  }

  private async loadFileFormats(): Promise<void> {
    try {
      const formats = await firstValueFrom(
        this.http.get<string[]>('/documents/model-fileformats.json'),
      );
      this.fileFormatOptions = (formats ?? [])
        .map((f) => String(f).trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((f) => ({ value: f.toUpperCase(), label: f.toUpperCase() }));
      this.cdr.markForCheck();
    } catch (error) {
      console.warn('Failed to load model file formats:', error);
    }
  }

  private extractFilterValues(response: unknown, field: string): string[] {
    const pick = (obj: Record<string, unknown> | null | undefined): unknown =>
      obj ? obj[field] : undefined;
    let data = response as Record<string, unknown>;
    const wrapped = response as { result?: unknown };
    if (wrapped?.result) {
      if (Array.isArray(wrapped.result) && wrapped.result.length > 0) {
        data = wrapped.result[0] as Record<string, unknown>;
      } else if (typeof wrapped.result === 'object') {
        data = wrapped.result as Record<string, unknown>;
      }
    }
    const raw = pick(data);
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return [...new Set(raw.map((v) => String(v).trim()).filter(Boolean))];
    }
    if (typeof raw === 'string' && raw.trim()) {
      return [raw.trim()];
    }
    return [];
  }

  isDropdownOpen(key: DropdownKey): boolean {
    return this.openDropdown() === key;
  }

  toggleDropdown(key: DropdownKey): void {
    this.openDropdown.update((current) => (current === key ? null : key));
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    this.openDropdown.set(null);
    this.cdr.markForCheck();
  }

  selectOption(key: DropdownKey, option: SelectOption): void {
    const controlName = key === 'modelType' ? 'modelType' : key;
    this.form.patchValue({ [controlName]: option.value });
    this.form.get(controlName)?.markAsTouched();
    this.closeDropdown();
    this.emitState();
  }

  isSelected(key: DropdownKey, value: string): boolean {
    const control = this.form.get(key === 'modelType' ? 'modelType' : key);
    const cur = control?.value ?? '';
    if (key === 'fileFormat') {
      return String(cur).toLowerCase() === String(value).toLowerCase();
    }
    return cur === value;
  }

  getSelectedLabel(key: DropdownKey): string {
    const find = (options: SelectOption[], value: string) =>
      options.find((o) => o.value === value)?.label ?? value;
    switch (key) {
      case 'industry':
        return find(this.industryOptions, this.form.controls.industry.value);
      case 'license':
        return find(this.licenseOptions, this.form.controls.license.value);
      case 'modelType':
        return find(this.modelTypeOptions, this.form.controls.modelType.value);
      case 'fileFormat': {
        const raw = this.form.controls.fileFormat.value;
        const option = this.fileFormatOptions.find(
          (o) => o.value.toLowerCase() === String(raw).toLowerCase(),
        );
        return option?.label ?? raw;
      }
      default:
        return '';
    }
  }

  isOtherLicenseSelected(): boolean {
    return this.form.controls.license.value === 'Other';
  }

  get tags(): string[] {
    return this.form.controls.tags.value;
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput).slice(
      0,
      this.tagMaxLength,
    );
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const current = [...this.form.controls.tags.value];
    if (!current.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      current.push(tag);
      this.form.patchValue({ tags: current });
      this.form.controls.tags.markAsTouched();
    }
    this.tagInput = '';
    this.emitState();
    this.cdr.markForCheck();
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  removeTag(tag: string): void {
    const updated = this.form.controls.tags.value.filter((t) => t !== tag);
    this.form.patchValue({ tags: updated });
    this.form.controls.tags.markAsTouched();
    this.emitState();
    this.cdr.markForCheck();
  }

  applyValue(value: ModelMetadataInfo): void {
    this.form.patchValue({
      industry: value.industry ?? '',
      license: value.license ?? '',
      otherLicense: value.otherLicense ?? '',
      modelType: value.modelType ?? '',
      fileFormat: value.fileFormat ?? '',
      tags: value.tags ?? [],
    });
    this.emitState();
    this.cdr.markForCheck();
  }

  validateAdditionalInfo(): boolean {
    return this.additionalInfoRef()?.validate() ?? true;
  }

  getAdditionalInfoItems(): AdditionalInfoFeedbackItem[] {
    return this.additionalInfoRef()?.getFeedbackItems() ?? [];
  }

  applyAdditionalInfo(state: TemplateAdditionalInfoState): void {
    this.additionalInfoRef()?.applyTemplate(state);
  }

  resetForm(): void {
    this.form.reset({
      industry: '',
      license: '',
      otherLicense: '',
      modelType: '',
      fileFormat: '',
      tags: [],
    });
    this.tagInput = '';
    this.openDropdown.set(null);
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.additionalInfoRef()?.resetForm();
    this.emitState();
    this.cdr.markForCheck();
  }

  showError(
    controlName:
      | 'industry'
      | 'license'
      | 'otherLicense'
      | 'modelType'
      | 'fileFormat'
      | 'tags',
  ): boolean {
    const c = this.form.get(controlName);
    return !!(c && c.invalid && c.touched);
  }

  private emitState(): void {
    this.validChange.emit(this.form.valid);
    this.valueChange.emit(this.form.getRawValue() as ModelMetadataInfo);
  }
}
